import React, { useState } from 'react'
import { Button } from '@wove/web-ui'
import { RefreshCw, AlertTriangle, Truck, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { 
  useCargowiseHandlingMethods,
  useCreateCargowiseHandlingMethod,
  useUpdateCargowiseHandlingMethod,
  useDeleteCargowiseHandlingMethod,
  CargowiseHandlingMethod,
} from '@wove/api'
import CargowiseHandlingMethodsTable from './CargowiseHandlingMethodsTable'
import CargowiseHandlingMethodFormDialog from './CargowiseHandlingMethodFormDialog'
import DeleteConfirmationDialog from '../CargowiseOrganizations/DeleteConfirmationDialog'

export default function CargowiseHandlingMethods() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [selectedHandlingMethod, setSelectedHandlingMethod] = useState<CargowiseHandlingMethod | null>(null)
  const [handlingMethodToDelete, setHandlingMethodToDelete] = useState<CargowiseHandlingMethod | null>(null)
  
  
  const {
    data: handlingMethods = [],
    isLoading,
    error,
    refetch,
  } = useCargowiseHandlingMethods()
  
  const createMutation = useCreateCargowiseHandlingMethod()
  const updateMutation = useUpdateCargowiseHandlingMethod()
  const deleteMutation = useDeleteCargowiseHandlingMethod()

  const handleCreate = () => {
    setSelectedHandlingMethod(null)
    setIsFormOpen(true)
  }

  const handleEdit = (handlingMethod: CargowiseHandlingMethod) => {
    setSelectedHandlingMethod(handlingMethod)
    setIsFormOpen(true)
  }

  const handleDelete = (handlingMethod: CargowiseHandlingMethod) => {
    setHandlingMethodToDelete(handlingMethod)
  }

  const handleFormSubmit = async (data: any) => {
    try {
      if (selectedHandlingMethod) {
        await updateMutation.mutateAsync({
          handlingMethodId: selectedHandlingMethod.id,
          data,
        })
        toast.success('Handling method updated successfully.')
      } else {
        await createMutation.mutateAsync(data)
        toast.success('Handling method created successfully.')
      }
      setIsFormOpen(false)
      setSelectedHandlingMethod(null)
    } catch (error) {
      toast.error(selectedHandlingMethod 
        ? 'Failed to update handling method. Please try again.'
        : 'Failed to create handling method. Please try again.')
    }
  }

  const handleDeleteConfirm = async () => {
    if (!handlingMethodToDelete) return

    try {
      await deleteMutation.mutateAsync(handlingMethodToDelete.id)
      toast.success('Handling method deleted successfully.')
      setHandlingMethodToDelete(null)
    } catch (error) {
      toast.error('Failed to delete handling method. Please try again.')
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">CargoWise Handling Methods</h2>
          <p className="text-muted-foreground">
            Browse CargoWise handling method data
          </p>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading handling methods
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">CargoWise Handling Methods</h2>
        <p className="text-muted-foreground">
          Browse and reference CargoWise handling method data
        </p>
      </div>

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium">All Handling Methods</h3>
          <span className="text-sm text-gray-500">
            {handlingMethods.length} method{handlingMethods.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleCreate}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Method
          </Button>
        </div>
      </div>

      {/* Handling Methods Table */}
      <CargowiseHandlingMethodsTable
        handlingMethods={handlingMethods}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Form Dialog */}
      <CargowiseHandlingMethodFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        handlingMethod={selectedHandlingMethod}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!handlingMethodToDelete}
        onOpenChange={(open) => !open && setHandlingMethodToDelete(null)}
        title="Delete Handling Method"
        description={`Are you sure you want to delete "${handlingMethodToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  )
}